import type { OverallTaskStatus } from '@kaiizen/kai-pro-api';
import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import {
  contractorAssignmentChangedDueToChangedDefaults,
  getTotalCostIfDefined,
  ScanGroupOptimized,
  ScanTaskOptimized,
  taskHasScopeChanges,
} from '@kaiizen/kai-scan-template-system';
import { useCallback, useMemo } from 'react';

import { useProjectStateLifecycle } from '@/Client/Layout/useProjectStateLifecycle';
import {
  DEFAULT_NEEDS_ATTENTION_TITLE,
  type ScopeAttentionFilterContext,
  taskNeedsAttentionFlag,
} from '@/Client/Lib/scopeAttentionFilterLib';
import { getGroupTitle } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatUsd, kaiPartialIdPresenter, taskNamePresenter } from '@/Client/Presenters';
import { useShowNotes } from '@/Client/State';
import { useConfirmationModal } from '@/Client/State/ConfirmationModalContext';
import { useToast } from '@/Client/State/ToastContext';
import { NeedsAttentionIndicator, TaskStatusBadge } from '@/Client/Widgets';
import { UndoIcon } from '@kaiizen/kai-shiki/react';
import { FocusedProgressUpdateModal } from './FocusedProgressUpdateModal/FocusedProgressUpdateModal';
import { useProgressUpdateModalState } from './FocusedProgressUpdateModal/useProgressUpdateModalState';
import { ScanCostDisplayWithDeltaSupportNew } from './ScanCostDisplayWithDeltaSupportNew';

import {
  OVERVIEW_HEADER_TITLE_ROW_RIGHT_STYLE,
  OVERVIEW_PROGRESS_UPDATE_BUTTON_STYLE,
  UPDATE_PROGRESS_LABEL,
} from './focusedOverviewHeaderStyles';

import {
  FOCUSED_TASK_CARD_AMOUNT,
  FOCUSED_TASK_CARD_AMOUNT_LINE,
  FOCUSED_TASK_CARD_EYEBROW,
  FOCUSED_TASK_CARD_EYEBROW_SEP,
  FOCUSED_TASK_CARD_EYEROW,
  FOCUSED_TASK_CARD_HEADER,
  FOCUSED_TASK_CARD_HEADER_CHIPS,
  FOCUSED_TASK_CARD_TITLE,
  FOCUSED_TASK_CARD_TITLE_NAME,
  FOCUSED_TASK_CARD_TITLE_ROW,
  FOCUSED_TASK_CARD_UNDO_BUTTON,
  FOCUSED_TASK_FLAG_STYLES,
} from './focusedTaskCardStyles';

// Single source of truth for this header's user-facing copy — referenced by
// tests instead of duplicating the literal strings, so a copy change only
// needs to happen here.
export const UNDO_CHANGES_LABEL = 'Cancel Task Changes';
export const RESOLVE_EDIT_REQUEST_LABEL = 'Resolve edit request';
export const UNDO_CHANGES_CONFIRM_TITLE = 'Cancel Task Changes';
export const UNDO_CHANGES_CONFIRM_MESSAGE = 'Are you sure you want to cancel the changes to this task?';
export { DELETE_TASK_CONFIRM_MESSAGE, DELETE_TASK_CONFIRM_TITLE, DELETE_TASK_LABEL } from './TaskDeleteButton';

// The eyebrow's own separator, exported for the same reason as the copy above it.
export const TASK_SEPARATOR = '·' as const;

export const FocusedTaskEditCardHeader = ({
  baselineTask,
  group,
  groupId,
  task,
  taskIsEditRequested,
  overallStatus,
}: {
  baselineTask?: ScanTaskOptimized;
  group?: ScanGroupOptimized;
  groupId: string;
  task: ScanTaskOptimized;
  taskIsEditRequested?: boolean;
  /** The status chip beside the title — THE TaskStatusBadge, same as every row. */
  overallStatus?: OverallTaskStatus;
}) => {
  const {
    scanIsEditable,
    scanDataMutators,
    defaultContractorsByTrade,
    baselineDefaultContractorsByTrade,
    showScanChanges,
    getContractorForTask,
    editRequestTaskIds,
    changedScopedTaskIds,
    baselineScanRoot,
  } = useScanEditingContextSlice([
    'scanIsEditable',
    'scanDataMutators',
    'defaultContractorsByTrade',
    'baselineDefaultContractorsByTrade',
    'showScanChanges',
    'getContractorForTask',
    'editRequestTaskIds',
    'changedScopedTaskIds',
    'baselineScanRoot',
  ]);
  const projectStateLifecycle = useProjectStateLifecycle();
  const { target, open, openForTask, close } = useProgressUpdateModalState();
  const showProgressUpdate = projectStateLifecycle === 'approved';
  const { showNotesEditor } = useShowNotes();
  const confirm = useConfirmationModal();
  const { tryWithErrorToast } = useToast();

  const cost = getTotalCostIfDefined(task);
  const baselineCost = getTotalCostIfDefined(baselineTask);

  const hasChanges = showScanChanges && scanIsEditable && taskHasScopeChanges(
    task,
    baselineTask ?? null,
    defaultContractorsByTrade,
    baselineDefaultContractorsByTrade,
  );

  const undoChanges = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (!scanIsEditable) return;
    if (!baselineTask || !baselineScanRoot) return;
    const contractorAssignmentChangedBecauseOfDefaults = contractorAssignmentChangedDueToChangedDefaults(
      task,
      baselineTask,
      defaultContractorsByTrade,
      baselineDefaultContractorsByTrade,
    );
    confirm({
      title: UNDO_CHANGES_CONFIRM_TITLE,
      message: UNDO_CHANGES_CONFIRM_MESSAGE,
      onConfirm: () => {
        tryWithErrorToast('cancel task changes', () => scanDataMutators.revertTaskScopeChanges(baselineScanRoot, groupId, task.id))
          .then(() => {
            if (contractorAssignmentChangedBecauseOfDefaults) {
              window.alert(
                "Can't cancel changes here because the default contractor assignment changed. Please cancel the default contractor changes on the project page.",
              );
            }
          });
      },
    });
  }, [
    groupId,
    task,
    baselineTask,
    baselineScanRoot,
    defaultContractorsByTrade,
    baselineDefaultContractorsByTrade,
    scanIsEditable,
    scanDataMutators,
    confirm,
    tryWithErrorToast,
  ]);

  const resolveEditRequest = (event: React.MouseEvent) => {
    event.stopPropagation();
    showNotesEditor({ scanPart: task, allowedNoteTypes: ['info', 'editResolution'] });
  };

  const category = task.template?.category;
  const evalContext = useMemo<ScopeAttentionFilterContext>(
    () => ({
      getContractorForTask,
      editRequestTaskIds: new Set(editRequestTaskIds ?? []),
      changeOrderTaskIds: new Set(showScanChanges ? changedScopedTaskIds ?? [] : []),
    }),
    [getContractorForTask, editRequestTaskIds, showScanChanges, changedScopedTaskIds],
  );
  const needsAttentionFlag = taskNeedsAttentionFlag(task, evalContext);
  const scopeIsSettled = projectStateLifecycle === 'approved' || projectStateLifecycle === 'closeout';

  const openProgressUpdate = useCallback(() => {
    openForTask({ groupId, group, task });
  }, [group, groupId, openForTask, task]);

  return (
    <>
      <div css={FOCUSED_TASK_CARD_HEADER}>
        {(group || category) && (
          <span css={FOCUSED_TASK_CARD_EYEROW} data-testid={'focused-task-eyerow'}>
            {scopeIsSettled && overallStatus && <TaskStatusBadge status={overallStatus} />}
            <span css={FOCUSED_TASK_CARD_EYEBROW} data-testid={'focused-task-eyebrow'}>
              {group && <>{getGroupTitle(group)}</>}
              {group && category && <span css={FOCUSED_TASK_CARD_EYEBROW_SEP}>{TASK_SEPARATOR}</span>}
              {category && <>{category}</>}
              {(group || category) && <span css={FOCUSED_TASK_CARD_EYEBROW_SEP}>{TASK_SEPARATOR}</span>}
              {kaiPartialIdPresenter(task.id)}
            </span>
          </span>
        )}
        <div css={FOCUSED_TASK_CARD_TITLE_ROW}>
          <div css={FOCUSED_TASK_CARD_TITLE}>
            <NeedsAttentionIndicator
              show={needsAttentionFlag}
              size={19}
              label={DEFAULT_NEEDS_ATTENTION_TITLE}
              styles={FOCUSED_TASK_FLAG_STYLES}
            />
            <span css={FOCUSED_TASK_CARD_TITLE_NAME}>{taskNamePresenter(task)}</span>
          </div>
          <div css={FOCUSED_TASK_CARD_HEADER_CHIPS}>
            {hasChanges && showScanChanges && (
              <button type={'button'} css={FOCUSED_TASK_CARD_UNDO_BUTTON} onClick={undoChanges}>
                <UndoIcon />
                {UNDO_CHANGES_LABEL}
              </button>
            )}
            {taskIsEditRequested && (
              <button type={'button'} css={FOCUSED_TASK_CARD_UNDO_BUTTON} onClick={resolveEditRequest}>
                {RESOLVE_EDIT_REQUEST_LABEL}
              </button>
            )}
          </div>
          <div css={OVERVIEW_HEADER_TITLE_ROW_RIGHT_STYLE}>
            {showProgressUpdate
              ? (
                <button
                  type={'button'}
                  css={OVERVIEW_PROGRESS_UPDATE_BUTTON_STYLE}
                  data-testid={'focused-task-progress-update-button'}
                  disabled={!scanIsEditable}
                  onClick={openProgressUpdate}
                >
                  {UPDATE_PROGRESS_LABEL}
                </button>
              )
              : null}
            <span css={FOCUSED_TASK_CARD_AMOUNT_LINE}>
              {showScanChanges && baselineTask
                ? (
                  <ScanCostDisplayWithDeltaSupportNew
                    cost={cost}
                    baselineCost={baselineCost}
                    showBaseline={!!(showScanChanges && baselineTask !== task)}
                  />
                )
                : cost != null
                ? <span css={FOCUSED_TASK_CARD_AMOUNT}>{formatUsd(cost)}</span>
                : null}
            </span>
          </div>
        </div>
      </div>
      {open && target
        ? <FocusedProgressUpdateModal target={target} onClose={close} />
        : null}
    </>
  );
};
